import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	HomeOutline,
	HStack,
	Link,
	MegaphoneOutline,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	rem,
	SearchOutline,
	Text,
} from '@bonusly/design-system';

import { ReactComponent as NavDragHandleIcon } from '../../../assets/images/nav-drag-handle.svg';
import { NavbarSettingsEditor } from '../../../components/navigation/navbar-settings-editor';
import { NAVBAR_FIXED_ITEM_IDS, NavbarConfigurationSource, NavbarItemId } from '../../../schemas/navigation';
import { SIDEBAR_API_TO_ITEM_ID, SIDEBAR_ITEM_TO_API_ID, SIDEBAR_ITEMS, SidebarItemId } from '../config/sidebar-items';

type CustomizeSidebarModalProps = {
	isOpen: boolean;
	onClose: () => void;
	availableItemIds: SidebarItemId[];
	mainNavItems: SidebarItemId[];
	moreMenuItems: SidebarItemId[];
	source: NavbarConfigurationSource;
	isSaving: boolean;
	onSave: (mainNavItems: SidebarItemId[], moreMenuItems: SidebarItemId[]) => Promise<void>;
	onReset: () => Promise<{ mainNavItems: SidebarItemId[]; moreMenuItems: SidebarItemId[] }>;
	onChange?: (mainNavItems: SidebarItemId[], moreMenuItems: SidebarItemId[]) => void;
};

export const CustomizeSidebarModal: FC<CustomizeSidebarModalProps> = ({
	isOpen,
	onClose,
	availableItemIds,
	mainNavItems,
	moreMenuItems,
	source,
	isSaving,
	onSave,
	onReset,
	onChange,
}) => {
	const { t } = useTranslation('navigation');
	const initialMainNavItemsRef = useRef<SidebarItemId[]>(mainNavItems);
	const initialMoreMenuItemsRef = useRef<SidebarItemId[]>(moreMenuItems);
	const draftMainNavItemsRef = useRef<SidebarItemId[]>(mainNavItems);
	const draftMoreMenuItemsRef = useRef<SidebarItemId[]>(moreMenuItems);
	const wasOpenRef = useRef(false);
	const [sessionMainNavItems, setSessionMainNavItems] = useState<SidebarItemId[]>(mainNavItems);
	const [sessionMoreMenuItems, setSessionMoreMenuItems] = useState<SidebarItemId[]>(moreMenuItems);
	const [editorResetKey, setEditorResetKey] = useState(0);

	useEffect(() => {
		const justOpened = isOpen && !wasOpenRef.current;
		wasOpenRef.current = isOpen;

		if (!justOpened) {
			return;
		}

		initialMainNavItemsRef.current = mainNavItems;
		initialMoreMenuItemsRef.current = moreMenuItems;
		draftMainNavItemsRef.current = mainNavItems;
		draftMoreMenuItemsRef.current = moreMenuItems;
		setSessionMainNavItems(mainNavItems);
		setSessionMoreMenuItems(moreMenuItems);
		setEditorResetKey((currentValue) => currentValue + 1);
	}, [isOpen, mainNavItems, moreMenuItems]);

	const availableItems = useMemo(() => {
		const availableItems = new Set(availableItemIds);
		return SIDEBAR_ITEMS.filter((item) => availableItems.has(item.id));
	}, [availableItemIds]);

	const editorItems = useMemo(
		() =>
			availableItems.map((item) => {
				const Icon = item.icon;
				return {
					id: item.apiId,
					label: t(item.translationKey),
					icon: <Icon size="md" />,
					infoText: t(`customizeSidebar.info.${item.id}`, { defaultValue: t(item.translationKey) }),
				};
			}),
		[availableItems, t]
	);

	const editorMainNavItems = useMemo(() => sessionMainNavItems.map((itemId) => SIDEBAR_ITEM_TO_API_ID[itemId]), [sessionMainNavItems]);
	const editorMoreMenuItems = useMemo(() => sessionMoreMenuItems.map((itemId) => SIDEBAR_ITEM_TO_API_ID[itemId]), [sessionMoreMenuItems]);

	const handleSave = async (nextMainNavItems: NavbarItemId[], nextMoreMenuItems: NavbarItemId[]) => {
		await onSave(
			nextMainNavItems.map((itemId) => SIDEBAR_API_TO_ITEM_ID[itemId]),
			nextMoreMenuItems.map((itemId) => SIDEBAR_API_TO_ITEM_ID[itemId])
		);
	};

	const handleChange = (nextMainNavItems: NavbarItemId[], nextMoreMenuItems: NavbarItemId[]) => {
		const mappedMainNavItems = nextMainNavItems.map((itemId) => SIDEBAR_API_TO_ITEM_ID[itemId]);
		const mappedMoreMenuItems = nextMoreMenuItems.map((itemId) => SIDEBAR_API_TO_ITEM_ID[itemId]);

		draftMainNavItemsRef.current = mappedMainNavItems;
		draftMoreMenuItemsRef.current = mappedMoreMenuItems;

		onChange?.(mappedMainNavItems, mappedMoreMenuItems);
	};

	const handleReset = async () => {
		const resetLayout = await onReset();
		initialMainNavItemsRef.current = resetLayout.mainNavItems;
		initialMoreMenuItemsRef.current = resetLayout.moreMenuItems;
		draftMainNavItemsRef.current = resetLayout.mainNavItems;
		draftMoreMenuItemsRef.current = resetLayout.moreMenuItems;
		setSessionMainNavItems(resetLayout.mainNavItems);
		setSessionMoreMenuItems(resetLayout.moreMenuItems);
		setEditorResetKey((currentValue) => currentValue + 1);
	};

	const isUsingUserPreferences = source === 'user';
	const fixedMainNavItems = useMemo(
		() => [
			{
				id: NAVBAR_FIXED_ITEM_IDS[0],
				label: t('links.announcements'),
				icon: <MegaphoneOutline size="md" />,
				infoText: t('customizeSidebar.info.announcements'),
			},
			{
				id: NAVBAR_FIXED_ITEM_IDS[1],
				label: t('links.home'),
				icon: <HomeOutline size="md" />,
				infoText: t('customizeSidebar.info.home'),
			},
			{
				id: NAVBAR_FIXED_ITEM_IDS[2],
				label: t('links.search'),
				icon: <SearchOutline size="md" />,
				infoText: t('customizeSidebar.info.search'),
			},
		],
		[t]
	);

	const handleModalClose = () => onClose();

	return (
		<Modal isOpen={isOpen} onClose={handleModalClose} size="xl" scrollBehavior="inside">
			<ModalOverlay />
			<ModalContent bg="bg.surface">
				<ModalHeader px={6} pt={rem(20)} pb={rem(12)} borderBottom={0}>
					<HStack justify="space-between" w="100%">
						<Text color="text.default" fontSize="xl" fontWeight="semibold" lineHeight={8}>
							{t('customizeSidebar.title')}
						</Text>
						<ModalCloseButton h={rem(32)} w={rem(32)} pos="static" borderRadius="full" />
					</HStack>
				</ModalHeader>
				<ModalBody px={6} pt={3} pb={6}>
					<Box mb={4}>
						<Text color="text.default" mb={1}>
							{t('customizeSidebar.modalDescription')}
						</Text>
						<Text color="text.soft">
							{t('customizeSidebar.learnMorePrefix')}{' '}
							<Link
								href="https://help.bonus.ly/en/articles/13778667-customize-your-navigation"
								color="text.interactive"
								fontWeight="medium"
								target="_blank"
								rel="noopener noreferrer"
							>
								{t('user_menu.helpCenter')}
							</Link>
						</Text>
					</Box>
					{editorItems.length === 0 ? (
						<Box py={3}>
							<Text color="text.soft">{t('customizeSidebar.noItems')}</Text>
						</Box>
					) : (
						<NavbarSettingsEditor
							key={`navbar-settings-editor-${editorResetKey}`}
							initialMainNavItems={editorMainNavItems}
							initialMoreMenuItems={editorMoreMenuItems}
							items={editorItems}
							mainNavTitle={t('customizeSidebar.mainNavTitle')}
							mainNavDescription=""
							moreMenuTitle={t('customizeSidebar.moreMenuTitle')}
							moreMenuDescription=""
							onSave={handleSave}
							onReset={handleReset}
							onChange={handleChange}
							isSaving={isSaving}
							resetButtonLabel={t('customizeSidebar.useDefaultOrder')}
							isResetDisabled={!isUsingUserPreferences}
							showSaveButton={false}
							isStackedLayout={true}
							dragHandlePosition="right"
							dragWholeItem={false}
							showItemBorder={false}
							showColumnBorder={false}
							columnPadding={0}
							fixedMainNavItems={fixedMainNavItems}
							dragHandleIcon={<NavDragHandleIcon />}
							useCompanyValuesHandleStyle={true}
							autoSaveOnDrop={true}
						/>
					)}
				</ModalBody>
			</ModalContent>
		</Modal>
	);
};
